/**
 * Virtual Assistant Component
 * Interactive chat interface for dashboard questions and investment guidance
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateAssistantResponse, getSuggestedQuestions } from '../utils/assistantResponses';

const VirtualAssistant = ({ isOpen, onClose, dashboardData }) => {
    const { t } = useTranslation();
    const assistantText = t('assistant', { returnObjects: true, defaultValue: {} }) || {};
    const headerText = assistantText.header || {};
    const buttonsText = assistantText.buttons || {};
    const placeholders = assistantText.placeholders || {};
    const suggestedText = assistantText.suggested || {};
    const actionsText = assistantText.actions || {};
    const initialMessageText = assistantText.initialMessage || "Hello! I'm your investment assistant. I can help you understand your dashboard, explain metrics, and provide insights about your portfolio strategy. What would you like to know?";

    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'assistant',
            text: initialMessageText
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const suggestedQuestions = getSuggestedQuestions(dashboardData || {});

    useEffect(() => {
        setMessages(prev => {
            if (prev.length === 1 && prev[0]?.type === 'assistant' && prev[0].text !== initialMessageText) {
                return [{ ...prev[0], text: initialMessageText }];
            }
            return prev;
        });
    }, [initialMessageText]);

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when assistant opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 100);
        }
    }, [isOpen]);

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            text: inputValue.trim()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setShowSuggestions(false);

        // Generate assistant response
        setTimeout(() => {
            const response = generateAssistantResponse(userMessage.text, dashboardData || {});
            const assistantMessage = {
                id: Date.now() + 1,
                type: 'assistant',
                text: response
            };
            setMessages(prev => [...prev, assistantMessage]);
        }, 500); // Small delay for better UX
    };

    const handleSuggestedQuestion = (question) => {
        setInputValue(question);
        setShowSuggestions(false);
        // Trigger send
        setTimeout(() => {
            const userMessage = {
                id: Date.now(),
                type: 'user',
                text: question
            };
            setMessages(prev => [...prev, userMessage]);
            
            setTimeout(() => {
                const response = generateAssistantResponse(question, dashboardData || {});
                const assistantMessage = {
                    id: Date.now() + 1,
                    type: 'assistant',
                    text: response
                };
                setMessages(prev => [...prev, assistantMessage]);
            }, 500);
        }, 100);
    };

    const handleClearChat = () => {
        setMessages([
            {
                id: 1,
                type: 'assistant',
                text: initialMessageText
            }
        ]);
        setShowSuggestions(true);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-end justify-end pointer-events-none">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />
            
            {/* Assistant Panel */}
            <div className="relative w-full max-w-md h-[600px] bg-[#2a2e32] border-t border-l border-r border-gray-700 rounded-t-2xl shadow-2xl flex flex-col pointer-events-auto transform transition-all duration-300 ease-out">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-[#212529] rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                        </div>
                        <div>
                                <h3 className="text-white font-semibold text-sm">{headerText.title || 'Investment Assistant'}</h3>
                                <p className="text-gray-400 text-xs">{headerText.subtitle || 'Ask me anything about your dashboard'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleClearChat}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-300 hover:bg-gray-700 transition-colors"
                                title={buttonsText.clear || 'Clear chat'}
                                aria-label={buttonsText.clear || 'Clear chat'}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-300 hover:bg-gray-700 transition-colors"
                                aria-label={buttonsText.close || 'Close'}
                                title={buttonsText.close || 'Close'}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                    message.type === 'user'
                                        ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                                        : 'bg-gray-700 text-gray-100'
                                }`}
                            >
                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">{message.text}</p>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Suggested Questions */}
                {showSuggestions && suggestedQuestions.length > 0 && (
                    <div className="px-4 pb-2 border-t border-gray-700 bg-[#212529]">
                        <div className="flex items-center justify-between py-2">
                            <p className="text-xs text-gray-400 font-medium">{suggestedText.title || 'Suggested questions:'}</p>
                            <button
                                onClick={() => setShowSuggestions(false)}
                                className="text-xs text-gray-500 hover:text-gray-400"
                            >
                                {suggestedText.hide || 'Hide'}
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2 pb-2">
                            {suggestedQuestions.slice(0, 4).map((question, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSuggestedQuestion(question)}
                                    className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-full transition-colors"
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input Area */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700 bg-[#212529]">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={placeholders.input || 'Ask a question...'}
                            className="flex-1 px-4 py-2.5 bg-gray-700 text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500 text-sm"
                        />
                        <button
                            type="submit"
                            disabled={!inputValue.trim()}
                            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm"
                        >
                            {actionsText.send || 'Send'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VirtualAssistant;

